import { useEffect, useState, type FormEvent } from 'react';
import { PencilLine } from 'lucide-react';
import { RENAME_PRICE } from '../../shared/economy.ts';
import { usernameKey, usernameProblem, USERNAME_MAX } from '../../shared/usernames.ts';
import { changeUsername, checkUsername, useAuth } from '../app/auth.ts';
import { useT } from '../app/preferences.ts';
import { Dialog } from './Dialog.tsx';
import { GoldAmount } from './PlayerLook.tsx';

type Availability = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'same';

/** Pick a new username for RENAME_PRICE gold. */
export function RenameDialog({ open, onClose, gold }: { open: boolean; onClose: () => void; gold: number }) {
  const t = useT();
  const { user, refresh } = useAuth();
  const current = user?.username ?? '';
  const [name, setName] = useState(current);
  const [availability, setAvailability] = useState<Availability>('same');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAfford = gold >= RENAME_PRICE;

  // Start from the current name each time the dialog opens.
  const [openedFor, setOpenedFor] = useState<boolean>(false);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) {
      setName(current);
      setAvailability('same');
      setError(null);
    }
  }

  // Ask the server a moment after typing stops. A different capitalisation of
  // your own name is yours already, so it is always available.
  useEffect(() => {
    if (availability !== 'checking') return;
    const id = window.setTimeout(async () => {
      if (usernameKey(name) === usernameKey(current)) {
        setAvailability('available');
        return;
      }
      try {
        const result = await checkUsername(name);
        setAvailability(result.available ? 'available' : result.problem ? 'invalid' : 'taken');
      } catch {
        setAvailability('idle');
      }
    }, 350);
    return () => window.clearTimeout(id);
  }, [name, current, availability]);

  const onChange = (value: string) => {
    const next = value.replace(/\s/g, '');
    setName(next);
    setError(null);
    setAvailability(next === current ? 'same' : usernameProblem(next) ? 'invalid' : 'checking');
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (availability !== 'available' || !canAfford) return;
    setSaving(true);
    const result = await changeUsername(name);
    setSaving(false);
    if (result.ok) {
      await refresh();
      onClose();
      return;
    }
    if (result.error === 'username_taken') setAvailability('taken');
    else if (result.error === 'username_invalid') setAvailability('invalid');
    else if (result.error === 'username_same') setAvailability('same');
    else if (result.error === 'not_enough_gold') setError(t('rename.notEnough', { price: RENAME_PRICE }));
    else setError(t('error.generic'));
  };

  const hint =
    availability === 'available'
      ? { text: t('welcome.available'), cls: 'ok' }
      : availability === 'taken'
        ? { text: t('welcome.taken'), cls: 'bad' }
        : availability === 'invalid'
          ? { text: t('welcome.invalid'), cls: 'bad' }
          : availability === 'same'
            ? { text: t('rename.same'), cls: '' }
            : availability === 'checking'
              ? { text: t('welcome.checking'), cls: '' }
              : { text: t('welcome.rules'), cls: '' };

  return (
    <Dialog open={open} onClose={onClose} title={t('rename.title')}>
      <form className="dialog-body rename-form" onSubmit={onSubmit}>
        <p>{t('rename.lead')}</p>
        <div className="field">
          <label className="field-label" htmlFor="rename-input">
            {t('welcome.label')}
          </label>
          <input
            id="rename-input"
            className="input"
            value={name}
            maxLength={USERNAME_MAX}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-invalid={availability === 'taken' || availability === 'invalid' ? true : undefined}
            aria-describedby="rename-hint"
            onChange={(e) => onChange(e.target.value)}
          />
          <p id="rename-hint" className={`username-hint ${hint.cls}`} role="status">
            {hint.text}
          </p>
        </div>

        <div className={`rename-price${canAfford ? '' : ' short'}`}>
          <span>{t('rename.cost')}</span>
          <strong>
            <GoldAmount amount={RENAME_PRICE} />
          </strong>
          <span className="rename-balance">
            {t('rename.balance')} <GoldAmount amount={gold} />
          </span>
        </div>
        {!canAfford && <p className="form-error">{t('rename.notEnough', { price: RENAME_PRICE })}</p>}
        {error && canAfford && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <div className="rename-actions">
          <button type="button" className="btn" onClick={onClose}>
            {t('shop.notNow')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving || !canAfford || availability !== 'available'}>
            {saving ? <span className="spinner" style={{ width: 22, height: 22 }} /> : <PencilLine size={20} />}
            {t('rename.confirm')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
